<template>
  <v-container fluid class="admin-page">
    <v-row class="align-center" justify="space-around">
      <v-col cols="6">
        <h2>Link Redirects</h2>
      </v-col>
      <v-col cols="6">
        <v-row>
          <v-col cols="8">
            <v-text-field
              v-model="search"
              label="Search redirect"
              append-icon="mdi-magnify"
              solo
              clearable
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="fill-height">
            <v-btn @click.prevent="fetchRedirects" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="2">
            <v-btn
              class="text--futura text--white text-capitalize"
              dark
              block
              @click="openAddDialog"
            >
              Add
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <Alert />
        <v-data-table
          :headers="headers"
          :items="filteredRedirects"
          :loading="loading"
          loading-text="Loading redirects..."
          :items-per-page="15"
          class="elevation-1"
        >
          <template v-slot:[`item.link`]="{ item }">
            <a :href="item.link" target="_blank" rel="noopener noreferrer">
              {{ item.link }}
            </a>
          </template>

          <template v-slot:[`item.url`]="{ item }">
            <a :href="item.url" target="_blank" rel="noopener noreferrer">
              {{ item.url }}
            </a>
          </template>

          <template v-slot:[`item.visible`]="{ item }">
            <v-switch
              :input-value="item.visible"
              inset
              hide-details
              @change="toggleVisible(item, $event)"
              :loading="Boolean(updatingVisibility[item.id])"
              :disabled="Boolean(updatingVisibility[item.id])"
            ></v-switch>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">Add Link Redirect</v-card-title>

        <v-card-text>
          <v-container fluid class="px-0">
            <v-row>
              <v-col cols="12">
                <v-radio-group v-model="form.mode" row>
                  <v-radio label="URL" value="url"></v-radio>
                  <v-radio label="Upload File" value="file"></v-radio>
                </v-radio-group>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Name"
                  dense
                  outlined
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.slug"
                  label="Slug"
                  dense
                  outlined
                  hint="Used for https://crystalsea.sg/redirect/[slug]"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-if="form.mode === 'url'">
                <v-text-field
                  v-model="form.url"
                  label="Actual URL"
                  dense
                  outlined
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-else>
                <v-file-input
                  v-model="form.file"
                  label="Upload File"
                  dense
                  outlined
                  prepend-icon="mdi-paperclip"
                  show-size
                  truncate-length="30"
                ></v-file-input>
              </v-col>

              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.visible"
                  inset
                  label="Visible"
                  hide-details
                ></v-switch>
              </v-col>

              <v-col cols="12">
                <div class="grey--text text--darken-1">
                  Redirect link preview:
                  <strong>{{ previewLink }}</strong>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddDialog">Cancel</v-btn>
          <v-btn dark :loading="saving" @click="saveRedirect">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Alert from "@/components/Alert.vue";
import server from "../api";

const createDefaultForm = () => ({
  mode: "url",
  name: "",
  slug: "",
  url: "",
  file: null,
  visible: true,
});

export default {
  name: "LinkRedirects",
  components: {
    Alert,
  },
  data() {
    return {
      loading: false,
      saving: false,
      search: "",
      showAddDialog: false,
      redirects: [],
      updatingVisibility: {},
      headers: [
        { text: "Name", value: "name" },
        { text: "Link", value: "link", sortable: false },
        { text: "Actual URL", value: "url", sortable: false },
        { text: "Visible", value: "visible", sortable: false },
      ],
      form: createDefaultForm(),
    };
  },
  computed: {
    filteredRedirects() {
      const keyword = this.search.trim().toLowerCase();

      if (!keyword) return this.redirects;

      return this.redirects.filter((item) => {
        return [item.name, item.slug, item.url, item.link]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword));
      });
    },
    previewLink() {
      return `https://crystalsea.sg/redirect/${this.form.slug || "[slug]"}`;
    },
  },
  methods: {
    normalizeVisible(value) {
      return value === true || value === 1 || value === "1";
    },
    normalizeSlug(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },
    normalizeRedirect(item = {}) {
      const slug = item.slug || "";

      return {
        ...item,
        id: item.id,
        name: item.name || "-",
        slug,
        url: item.url || "-",
        visible: this.normalizeVisible(item.visible),
        link: `https://crystalsea.sg/redirect/${slug}`,
      };
    },
    getAuthConfig(extra = {}) {
      return {
        ...extra,
        headers: {
          token: localStorage.token,
          ...(extra.headers || {}),
        },
      };
    },
    getErrorMessage(err, fallback) {
      return (
        (err.response &&
          err.response.data &&
          err.response.data.errors &&
          (Array.isArray(err.response.data.errors)
            ? err.response.data.errors[0].message
            : err.response.data.errors.message || err.response.data.errors)) ||
        (err.response && err.response.data && err.response.data.message) ||
        fallback
      );
    },
    fetchRedirects() {
      this.loading = true;

      server
        .get("/linktree/linkredirect")
        .then((response) => {
          const result = response.data.result || [];
          this.redirects = result.map(this.normalizeRedirect);
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to load link redirects")
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openAddDialog() {
      this.form = createDefaultForm();
      this.showAddDialog = true;
    },
    closeAddDialog() {
      this.showAddDialog = false;
      this.form = createDefaultForm();
    },
    validateForm() {
      if (!this.form.name.trim()) {
        this.$store.commit("SET_ERROR", "Name is required");
        return false;
      }

      if (!this.form.slug.trim()) {
        this.$store.commit("SET_ERROR", "Slug is required");
        return false;
      }

      if (this.form.mode === "url" && !this.form.url.trim()) {
        this.$store.commit("SET_ERROR", "Actual URL is required");
        return false;
      }

      if (this.form.mode === "file" && !this.form.file) {
        this.$store.commit("SET_ERROR", "File is required");
        return false;
      }

      return true;
    },
    findExistingRedirectBySlug(slug) {
      const normalizedSlug = this.normalizeSlug(slug);

      return this.redirects.find(
        (item) => this.normalizeSlug(item.slug) === normalizedSlug
      );
    },
    confirmOverwrite(existingRedirect) {
      if (!existingRedirect) return true;

      return window.confirm(
        `Slug "${existingRedirect.slug}" already exists for "${existingRedirect.name}". Continuing will overwrite the existing data. Do you want to continue?`
      );
    },
    saveRedirect() {
      if (!this.validateForm()) return;

      const slug = this.form.slug.trim();
      const existingRedirect = this.findExistingRedirectBySlug(slug);

      if (!this.confirmOverwrite(existingRedirect)) {
        return;
      }

      this.saving = true;

      if (this.form.mode === "file") {
        const data = new FormData();
        data.append("name", this.form.name.trim());
        data.append("slug", slug);
        data.append("visible", this.form.visible ? 1 : 0);
        data.append("file", this.form.file);

        server
          .post(
            "/linktree/linkredirect/upload-document",
            data,
            this.getAuthConfig({
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
          )
          .then(() => {
            this.$store.commit("SET_SUCCESS", "Link redirect added successfully");
            this.closeAddDialog();
            this.fetchRedirects();
          })
          .catch((err) => {
            this.$store.commit(
              "SET_ERROR",
              this.getErrorMessage(err, "Failed to upload redirect document")
            );
          })
          .finally(() => {
            this.saving = false;
          });

        return;
      }

      server
        .post(
          "/linktree/linkredirect",
          {
            name: this.form.name.trim(),
            slug,
            url: this.form.url.trim(),
            visible: this.form.visible ? 1 : 0,
          },
          this.getAuthConfig()
        )
        .then(() => {
          this.$store.commit("SET_SUCCESS", "Link redirect added successfully");
          this.closeAddDialog();
          this.fetchRedirects();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to add link redirect")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    toggleVisible(item, value) {
      const previousVisible = item.visible;
      const nextVisible = this.normalizeVisible(value);

      this.$set(this.updatingVisibility, item.id, true);
      item.visible = nextVisible;

      server
        .put(
          `/linktree/linkredirect/${item.id}`,
          {
            visible: nextVisible ? 1 : 0,
          },
          this.getAuthConfig()
        )
        .then(() => {
          this.$store.commit(
            "SET_SUCCESS",
            `Visibility updated for ${item.name}`
          );
        })
        .catch((err) => {
          item.visible = previousVisible;
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to update visibility")
          );
        })
        .finally(() => {
          this.$delete(this.updatingVisibility, item.id);
        });
    },
  },
  created() {
    this.fetchRedirects();
  },
};
</script>

<style scoped>
a {
  color: inherit;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
